/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import {
  CheckOutlined,
  ClearOutlined,
  Edit,
  DeleteOutline,
  CheckCircleOutlineOutlined,
  BlockOutlined,
} from '@material-ui/icons';
import { connect } from 'react-redux';
import {
  saveMedication,
  updateMedication,
  inactivateMedication,
  activateMedication,
  deleteMedication,
  getPatientInactiveMedication,
  getPatientActiveMedications,
} from 'services/soap/action';

// ---------------------------------------------------------------------------
// TipButton: IconButton wrapped in a Tooltip.
// The <span> wrapper ensures the tooltip renders even when the button is disabled.
// ---------------------------------------------------------------------------
const TipButton = ({ title, children, ...rest }) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex' }}>
      <IconButton {...rest}>{children}</IconButton>
    </span>
  </Tooltip>
);

const Medications = props => {
  const {
    classes,
    patientId,
    getPatientInactiveMedication,
    getPatientActiveMedications,
    ptActiveMedication,
    ptInactiveMedication,
  } = props;

  const initialUserState = { id: 0, name: '', dose: '', directions: '' };

  const [values, setValues] = useState(initialUserState);
  const [editvalues, setEditValues] = useState(initialUserState);
  const [medication, setMedication] = useState([]);
  const [inactivemedication, setInactiveMedication] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  // ---------------------------------------------------------------------------
  // Loading states
  // 'save'         — header row save button
  // 'update'       — inline update confirm button
  // item.id        — inactivate / activate spinner on that specific row
  // `delete-{id}`  — delete spinner on that specific row
  // ---------------------------------------------------------------------------
  const [loadingId, setLoadingId] = useState(null);

  // ---------------------------------------------------------------------------
  // Sync Redux → local state
  // Remove the `length > 0` guard so that when a list becomes empty (e.g. last
  // active item is inactivated) the local state is cleared too. Without this,
  // the stale local array stays in state while Redux has already updated,
  // causing both lists to show the same item simultaneously.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (ptActiveMedication !== undefined)
      setMedication(ptActiveMedication || []);
  }, [ptActiveMedication]);

  useEffect(() => {
    if (ptInactiveMedication !== undefined)
      setInactiveMedication(ptInactiveMedication || []);
  }, [ptInactiveMedication]);

  // ---------------------------------------------------------------------------
  // Field handlers
  // ---------------------------------------------------------------------------
  const handleFieldChange = (field, value) =>
    setValues({ ...values, [field]: value });

  const handleEditFieldChange = (field, value) =>
    setEditValues({ ...editvalues, [field]: value });

  // ---------------------------------------------------------------------------
  // Save new medication
  // ---------------------------------------------------------------------------
  const handleSaveMedication = async () => {
    if (values.name || values.dose || values.directions) {
      setLoadingId('save');
      try {
        const res = await props.saveMedication(patientId, values);
        if (res.status) {
          await Promise.all([
            getPatientInactiveMedication(patientId),
            getPatientActiveMedications(patientId),
          ]);
          setValues(initialUserState);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Update existing medication
  // ---------------------------------------------------------------------------
  const handleUpdateMedication = async idx => {
    if (editvalues.name || editvalues.dose) {
      setLoadingId('update');
      try {
        const res = await props.updateMedication(patientId, editvalues);
        if (res.status) {
          setIsEdit(false);
          await Promise.all([
            getPatientInactiveMedication(patientId),
            getPatientActiveMedications(patientId),
          ]);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Inactivate active medication
  // ---------------------------------------------------------------------------
  const handleInactivateMedication = async item => {
    if (loadingId !== null) return;
    setLoadingId(item.id);
    try {
      const res = await props.inactivateMedication(patientId, item);
      if (res.status) {
        await Promise.all([
          getPatientInactiveMedication(patientId),
          getPatientActiveMedications(patientId),
        ]);
      }
    } finally {
      setLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Activate inactive medication
  // ---------------------------------------------------------------------------
  const handleActivateMedication = async item => {
    if (loadingId !== null) return;
    setLoadingId(item.id);
    try {
      const res = await props.activateMedication(patientId, item);
      if (res.status) {
        await Promise.all([
          getPatientInactiveMedication(patientId),
          getPatientActiveMedications(patientId),
        ]);
      }
    } finally {
      setLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete inactive medication
  // ---------------------------------------------------------------------------
  const handleDeleteMedication = async id => {
    if (loadingId !== null) return;
    setLoadingId(`delete-${id}`);
    try {
      const res = await props.deleteMedication(patientId, id);
      if (res.status) {
        await Promise.all([
          getPatientInactiveMedication(patientId),
          getPatientActiveMedications(patientId),
        ]);
        setValues(initialUserState);
      }
    } finally {
      setLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit / clear
  // ---------------------------------------------------------------------------
  const handleEditMedication = (data, index) => {
    setIsEdit(true);
    if (data)
      setEditValues({
        id: data.id,
        name: data.name,
        dose: data.dose,
        directions: data.directions,
        editIndex: index,
      });
  };

  const clearMedication = () => {
    setIsEdit(false);
    setValues(initialUserState);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={classes.templateWrap}>
      <div className={classes.templateBody} style={{ borderTop: 'default' }}>
        <PerfectScrollbar>
          <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
            <Table className={classes.templateTable}>

              {/* ============================================================
                  HEADER ROW — add new medication
              ============================================================ */}
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('name', e.target.value)}
                      placeholder="Input Name"
                      value={values.name}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('dose', e.target.value)}
                      placeholder="Input dose"
                      value={values.dose}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('directions', e.target.value)}
                      placeholder="Input directions"
                      value={values.directions}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                    <TipButton
                      title="Save"
                      disabled={loadingId === 'save'}
                      onClick={handleSaveMedication}
                      size="small"
                      style={{ color: '#03A484' }}>
                      {loadingId === 'save'
                        ? <CircularProgress size={16} color="inherit" />
                        : <CheckOutlined />}
                    </TipButton>
                    <TipButton
                      title="Clear"
                      disabled={loadingId === 'save'}
                      onClick={clearMedication}
                      size="small">
                      <ClearOutlined style={{ color: '#DE5555' }} />
                    </TipButton>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>

                {/* ==========================================================
                    ACTIVE MEDICATIONS
                ========================================================== */}
                {medication &&
                  medication.length > 0 &&
                  medication
                    .sort((a, b) => b.id - a.id)
                    .map((item, idx) => (
                      <TableRow key={item.id}>
                        {isEdit && idx === editvalues.editIndex ? (
                          <>
                            <TableCell width="25%">
                              <TextField
                                className={classes.textBox}
                                InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                                onChange={e => handleEditFieldChange('name', e.target.value)}
                                placeholder="Input name"
                                value={editvalues.name}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                className={classes.textBox}
                                InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                                onChange={e => handleEditFieldChange('dose', e.target.value)}
                                placeholder="Input dose"
                                value={editvalues.dose}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                className={classes.textBox}
                                InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                                onChange={e => handleEditFieldChange('directions', e.target.value)}
                                placeholder="Input directions"
                                value={editvalues.directions}
                                variant="outlined"
                              />
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.dose}</TableCell>
                            <TableCell>{item.directions}</TableCell>
                          </>
                        )}
                        <TableCell align="center" width={72}>
                          {isEdit && idx === editvalues.editIndex ? (
                            <>
                              <TipButton
                                title="Update Medication"
                                disabled={loadingId === 'update'}
                                onClick={() => handleUpdateMedication(idx)}
                                size="small"
                                style={{ color: '#03A484' }}>
                                {loadingId === 'update'
                                  ? <CircularProgress size={16} color="inherit" />
                                  : <CheckOutlined />}
                              </TipButton>
                              <TipButton
                                title="Clear"
                                disabled={loadingId === 'update'}
                                onClick={clearMedication}
                                size="small">
                                <ClearOutlined style={{ color: '#DE5555' }} />
                              </TipButton>
                            </>
                          ) : (
                            <>
                              <TipButton
                                title="Mark as Inactive"
                                disabled={loadingId !== null}
                                onClick={() => handleInactivateMedication(item)}
                                size="small">
                                {loadingId === item.id
                                  ? <CircularProgress size={16} color="inherit" />
                                  : <CheckCircleOutlineOutlined />}
                              </TipButton>
                              <TipButton
                                title="Edit"
                                disabled={loadingId !== null}
                                onClick={() => handleEditMedication(item, idx)}
                                size="small">
                                <Edit color="primary" />
                              </TipButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                {/* ==========================================================
                    INACTIVE MEDICATIONS
                ========================================================== */}
                {inactivemedication &&
                  inactivemedication.length > 0 &&
                  inactivemedication.map((item, idx) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.dose}</TableCell>
                      <TableCell>{item.directions}</TableCell>
                      <TableCell align="center" width={72}>
                        <TipButton
                          title="Mark as Active"
                          disabled={loadingId !== null}
                          onClick={() => handleActivateMedication(item)}
                          size="small">
                          {loadingId === item.id
                            ? <CircularProgress size={16} color="inherit" />
                            : <BlockOutlined style={{ color: '#ccc' }} />}
                        </TipButton>
                        <TipButton
                          title="Delete"
                          disabled={loadingId !== null}
                          onClick={() => handleDeleteMedication(item.id)}
                          size="small">
                          {loadingId === `delete-${item.id}`
                            ? <CircularProgress size={16} color="inherit" />
                            : <DeleteOutline style={{ color: '#DE5555' }} />}
                        </TipButton>
                      </TableCell>
                    </TableRow>
                  ))}

              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

Medications.propTypes = {
  className: PropTypes.string,
};

const mapDispatchToProps = {
  saveMedication,
  updateMedication,
  inactivateMedication,
  activateMedication,
  deleteMedication,
  getPatientInactiveMedication,
  getPatientActiveMedications,
};

const mapStateToProps = state => ({
  ptInactiveMedication: state.soap && state.soap.ptInactiveMedication,
  ptActiveMedication: state.soap && state.soap.ptActiveMedication,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Medications))
);